import React, { useState } from "react";
import { doc, updateDoc } from "../../../config/dataClient";
import { Edit2, X, Lock } from "lucide-react";
import { db } from "../../../config/firebase";
import { APP_ID } from "../../../config/constants";
import RocDateInput from "../../ui/RocDateInput";
import ProfileDetailsModal from "./ProfileDetailsModal";

// 員工編輯個人資料 Modal (限制版)
export default function EditProfileModal({ user, userData, canEdit = false, onClose, onSubmitted }) {
  const [formData, setFormData] = useState({
    fullName: userData?.fullName || "",
    gender: userData?.gender || "",
    idNumber: userData?.idNumber || "",
    bloodType: userData?.bloodType || "",
    birthday: userData?.birthday || "",
    education: userData?.education || "",
    workExperience: userData?.workExperience || [],
    familyMembers: userData?.familyMembers || [],
    bankAccount: userData?.bankAccount || "",
    mobile: userData?.mobile || "",
    address: userData?.address || "",
    personalEmail: userData?.personalEmail || "",
    emergencyContact:
      userData?.emergencyContact || { name: "", relation: "", mobile: "" },
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (field, value) =>
    setFormData((p) => ({ ...p, [field]: value }));
  const handleObjChange = (obj, field, val) =>
    setFormData((p) => ({ ...p, [obj]: { ...p[obj], [field]: val } }));

  const handleSave = async () => {
    if (!canEdit || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await updateDoc(
        doc(db, "artifacts", APP_ID, "users", user.uid, "profile", "main"),
        {
          pendingProfileUpdate: {
            ...formData,
            updatedAt: new Date().toISOString(),
          },
        }
      );
      onSubmitted?.();
      onClose();
    } catch (e) {
      alert("送出失敗");
      console.error(e);
    } finally {
      setIsSubmitting(false);
    }
  };

  const disabledStyle =
    "w-full border rounded p-2 text-sm bg-slate-100 text-slate-500 cursor-not-allowed";

  if (!canEdit) return <ProfileDetailsModal userData={userData} onClose={onClose} />;

  return (
    <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-lg rounded-xl shadow-2xl max-h-[90vh] overflow-y-auto p-6">
        <div className="flex justify-between items-center mb-4 border-b pb-2">
          <h3 className="font-bold text-lg text-slate-800">編輯個人資料</h3>
          <button onClick={onClose}>
            <X size={20} className="text-slate-400" />
          </button>
        </div>

        <div className="space-y-4 mb-6">
          <div className="bg-yellow-50 p-2 rounded text-xs text-yellow-700 mb-2 flex items-center gap-2">
            <Lock size={14} /> 部分人事資料鎖定，如需修改請洽管理員。
          </div>

          {/* 可編輯區塊 */}
          <div className="space-y-4 border-b border-slate-100 pb-4">
            <h4 className="text-sm font-bold text-blue-600 flex items-center gap-1">
              <Edit2 size={14} /> 可編輯項目
            </h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-1">
                <label className="text-xs font-bold text-slate-600 block mb-1">
                  手機號碼
                </label>
                <input
                  className="w-full border rounded p-2 text-sm focus:ring-2 focus:ring-blue-100 outline-none"
                  value={formData.mobile}
                  onChange={(e) => handleChange("mobile", e.target.value)}
                />
              </div>
              <div className="col-span-1">
                <label className="text-xs font-bold text-slate-600 block mb-1">
                  Email
                </label>
                <input
                  className="w-full border rounded p-2 text-sm focus:ring-2 focus:ring-blue-100 outline-none"
                  value={formData.personalEmail}
                  onChange={(e) => handleChange("personalEmail", e.target.value)}
                />
              </div>
              <div className="col-span-2">
                <label className="text-xs font-bold text-slate-600 block mb-1">
                  通訊地址
                </label>
                <input
                  className="w-full border rounded p-2 text-sm focus:ring-2 focus:ring-blue-100 outline-none"
                  value={formData.address}
                  onChange={(e) => handleChange("address", e.target.value)}
                />
              </div>
            </div>

            {/* 緊急聯絡人 (可編輯) */}
            <div className="bg-blue-50 p-3 rounded border border-blue-100">
              <div className="text-xs font-bold text-blue-700 mb-2">
                緊急聯絡人
              </div>
              <div className="flex gap-2">
                <input
                  className="w-20 border rounded p-1.5 text-xs focus:ring-2 focus:ring-blue-100 outline-none"
                  placeholder="姓名"
                  value={formData.emergencyContact.name}
                  onChange={(e) =>
                    handleObjChange("emergencyContact", "name", e.target.value)
                  }
                />
                <input
                  className="w-20 border rounded p-1.5 text-xs focus:ring-2 focus:ring-blue-100 outline-none"
                  placeholder="關係"
                  value={formData.emergencyContact.relation}
                  onChange={(e) =>
                    handleObjChange("emergencyContact", "relation", e.target.value)
                  }
                />
                <input
                  className="flex-1 border rounded p-1.5 text-xs focus:ring-2 focus:ring-blue-100 outline-none"
                  placeholder="手機"
                  value={formData.emergencyContact.mobile}
                  onChange={(e) =>
                    handleObjChange("emergencyContact", "mobile", e.target.value)
                  }
                />
              </div>
            </div>
          </div>

          {/* 不可編輯區塊 */}
          <div className="space-y-4 opacity-75">
            <h4 className="text-sm font-bold text-slate-400 flex items-center gap-1">
              <Lock size={14} /> 基本資料 (唯讀)
            </h4>

            <div className="bg-slate-50 p-2 rounded border border-slate-200 text-xs text-slate-600">
              <div className="flex justify-between mb-1">
                <span>銀行: 國泰世華 013</span>
                <span>戶名: {formData.fullName}</span>
              </div>
              <div className="font-bold">
                帳號: {formData.bankAccount || "(未填寫)"}
              </div>
            </div>

            <div>
              <label className="text-xs font-bold text-slate-400 block mb-1">
                中文全名
              </label>
              <input className={disabledStyle} value={formData.fullName} disabled />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold text-slate-400 block mb-1">
                  性別
                </label>
                <select className={disabledStyle} value={formData.gender} disabled>
                  <option value="">未填寫</option>
                  <option value="male">男</option>
                  <option value="female">女</option>
                  <option value="other">其他</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-bold text-slate-400 block mb-1">
                  血型
                </label>
                <select className={disabledStyle} value={formData.bloodType} disabled>
                  <option value="">未填寫</option>
                  <option value="A">A型</option>
                  <option value="B">B型</option>
                  <option value="O">O型</option>
                  <option value="AB">AB型</option>
                  <option value="Unknown">未知</option>
                </select>
              </div>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-400 block mb-1">
                身份證字號
              </label>
              <input className={disabledStyle} value={formData.idNumber} disabled />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-400 block mb-1">
                出生年月日（民國）
              </label>
              <RocDateInput
                className={disabledStyle}
                value={formData.birthday}
                disabled
              />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-400 block mb-1">
                畢業學校及科系
              </label>
              <input className={disabledStyle} value={formData.education} disabled />
            </div>

            {formData.workExperience?.length > 0 && (
              <div className="bg-slate-50 p-3 rounded border border-slate-200">
                <div className="text-xs font-bold text-slate-500 mb-2">
                  工作經歷
                </div>
                {formData.workExperience.map((w, i) => (
                  <div
                    key={i}
                    className="mb-2 last:mb-0 p-2 bg-white rounded border border-slate-200 text-xs text-slate-600"
                  >
                    <div className="font-bold mb-1">{w.company || "--"}</div>
                    <div className="flex justify-between text-slate-400">
                      <span>{w.job || "--"}</span>
                      <span>{w.period || "--"}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {formData.familyMembers?.length > 0 && (
              <div className="bg-slate-50 p-3 rounded border border-slate-200">
                <div className="text-xs font-bold text-slate-500 mb-2">
                  家庭成員
                </div>
                {formData.familyMembers.map((f, i) => (
                  <div
                    key={i}
                    className="flex gap-2 mb-2 items-center text-xs text-slate-600"
                  >
                    <div className="w-1/3 p-1.5 bg-slate-100 rounded border border-slate-200 truncate">
                      {f.name}
                    </div>
                    <div className="flex-1 p-1.5 bg-slate-100 rounded border border-slate-200 truncate">
                      {f.title}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 bg-slate-100 text-slate-600 py-2 rounded-lg font-bold hover:bg-slate-200"
          >
            取消
          </button>
          <button
            onClick={handleSave}
            disabled={isSubmitting}
            className="flex-1 bg-blue-600 text-white py-2 rounded-lg font-bold hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed"
          >
            {isSubmitting ? "送出中..." : "送出審核"}
          </button>
        </div>
      </div>
    </div>
  );
}
